import React, { useContext, useEffect, useState, useMemo } from "react";
import { Container, Card, Row, Col, Form, Button, Table, Spinner, Badge } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import AppBreadcrumb from "../components/AppBreadcrumb";
import { AuthContext } from "../context/AuthContext";
import axios from "../context/AxiosInstance";
import RhTablePagination from "../components/common/RhTablePagination";

const MOIS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

const DeverouillerSalairePage = () => {
  const { role } = useContext(AuthContext);
  const isAdmin = (role || "").toUpperCase() === "ADMIN";

  const [employes, setEmployes] = useState([]);

  // Sélections
  const [allEmployes, setAllEmployes] = useState(false);
  const [selectedEmployes, setSelectedEmployes] = useState([]); // [{value,label}]
  const [allMois, setAllMois] = useState(false);
  const [selectedMois, setSelectedMois] = useState([]); // [{value,label}]
  const [annee, setAnnee] = useState("");

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);

  const formatDateTimeFr = (value) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric', month: 'long', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).format(d);
    } catch { return String(value); }
  };

  const getEmployeName = (row) => {
    const nom = row.nom_employe || row.nom || row.employe?.nom || "";
    const prenom = row.prenom_employe || row.prenom || row.employe?.prenom || "";
    return `${nom} ${prenom}`.trim() || "—";
  };
  const getMatricule = (row) => row.matricule_employe || row.matricule || "—";
  const getPeriode = (row) => `${row.mois || row.mois_salaire || ""} ${row.annee || row.annee_salaire || ""}`.trim() || "—";
  const getLockedAt = (row) => row.date_verrouillage || row.created_at || "—";
  const getLockerLogin = (row) => row.verrouilleur_login || row.locked_by_login || "—";
  const getLocker = (row) => row.verrouilleur || row.verrouilleur_nom || "—";

  const employeOptions = useMemo(
    () => employes.map((e) => ({ value: e.id_employe, label: `${e.nom || ''} ${e.prenom || ''}`.trim() })),
    [employes]
  );
  const moisOptions = useMemo(() => MOIS.map((m) => ({ value: m, label: m })), []);

  useEffect(() => {
    const fetchEmployes = async () => {
      try {
        const res = await axios.get("/employes/");
        setEmployes(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch {}
    };
    fetchEmployes();
  }, []);

  // Charge tous les salaires verrouillés (filtre par année si choisie)
  const loadLocked = async () => {
    setLoading(true);
    try {
      const params = {};
      if (annee) params.annee = annee;
      const res = await axios.get("/salaires/verrouilles/", { params });
      setItems(Array.isArray(res.data?.data) ? res.data.data : []);
      setCurrentPage(1);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadLocked(); /* eslint-disable-next-line */ }, [annee]);

  // Filtrage d'affichage selon la sélection
  const filteredItems = useMemo(() => {
    const empIds = selectedEmployes.map((s) => s.value);
    const moisVals = selectedMois.map((s) => s.value);
    return items.filter((it) => {
      const empOk = allEmployes || empIds.length === 0 || empIds.includes(it.id_employe);
      const moisOk = allMois || moisVals.length === 0 || moisVals.includes(it.mois || it.mois_salaire);
      return empOk && moisOk;
    });
  }, [items, allEmployes, selectedEmployes, allMois, selectedMois]);

  const empSelectionOk = allEmployes || selectedEmployes.length > 0;
  const moisSelectionOk = allMois || selectedMois.length > 0;
  const canUnlock = empSelectionOk && moisSelectionOk && filteredItems.length > 0;

  const resetSelection = () => {
    setAllEmployes(false); setSelectedEmployes([]);
    setAllMois(false); setSelectedMois([]);
    setAnnee("");
  };

  const handleUnlock = async () => {
    const empLabel = allEmployes ? "TOUS les employés" : `${selectedEmployes.length} employé(s)`;
    const moisLabel = allMois ? "TOUS les mois" : selectedMois.map((m) => m.value).join(", ");
    const anneeLabel = annee ? annee : "toutes les années";

    const confirm = await Swal.fire({
      title: "Confirmer le déverrouillage",
      html: `Vous allez <b>déverrouiller</b> et <b>supprimer les bulletins associés</b> pour :<br/><br/>`
        + `👤 ${empLabel}<br/>📅 ${moisLabel}<br/>🗓️ ${anneeLabel}<br/><br/>`
        + `<b>${filteredItems.length}</b> ligne(s) verrouillée(s) concernée(s). Cette action est irréversible.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Oui, déverrouiller",
      cancelButtonText: "Annuler",
    });
    if (!confirm.isConfirmed) return;

    setProcessing(true);
    try {
      const payload = {
        id_employes: allEmployes ? [] : selectedEmployes.map((s) => Number(s.value)),
        mois: allMois ? [] : selectedMois.map((s) => s.value),
        annee: annee ? Number(annee) : null,
      };
      const res = await axios.post("/salaires/deverrouiller-multiple", payload);
      const d = res.data || {};
      await Swal.fire({
        title: "Déverrouillage effectué ✅",
        html: `🔓 Salaires déverrouillés : <b>${d.deleted_verrou ?? 0}</b><br/>🗑️ Bulletins supprimés : <b>${d.deleted_bulletins ?? 0}</b>`,
        icon: "success",
      });
      await loadLocked();
    } catch (err) {
      Swal.fire("Erreur", err.response?.data?.detail || "Échec du déverrouillage", "error");
    } finally { setProcessing(false); }
  };

  if (!isAdmin) return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Navbar />
        <Container fluid className="p-5">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Déverrouiller salaire" }]} />
          <div className="alert alert-danger">Accès réservé aux administrateurs.</div>
        </Container>
      </div>
    </div>
  );

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Navbar />
        <Container fluid className="p-5">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Déverrouiller un salaire" }]} />
          <h4 className="fw-bold mb-3">🔓 Déverrouiller des salaires</h4>

          <Card className="mb-3 shadow-sm surface-card toolbar-card">
            <Card.Body>
              <Row className="g-3 align-items-start">
                <Col md={5}>
                  <Form.Group>
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Label className="mb-1">Employés</Form.Label>
                      <Form.Check
                        type="switch"
                        id="all-employes"
                        label="Tous les employés"
                        checked={allEmployes}
                        onChange={(e) => { setAllEmployes(e.target.checked); if (e.target.checked) setSelectedEmployes([]); }}
                      />
                    </div>
                    <Select
                      isMulti
                      isDisabled={allEmployes}
                      classNamePrefix="react-select"
                      placeholder="Sélectionner un ou plusieurs employés..."
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                      options={employeOptions}
                      value={selectedEmployes}
                      onChange={(opts) => setSelectedEmployes(opts || [])}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Label className="mb-1">Mois</Form.Label>
                      <Form.Check
                        type="switch"
                        id="all-mois"
                        label="Tous les mois"
                        checked={allMois}
                        onChange={(e) => { setAllMois(e.target.checked); if (e.target.checked) setSelectedMois([]); }}
                      />
                    </div>
                    <Select
                      isMulti
                      isDisabled={allMois}
                      classNamePrefix="react-select"
                      placeholder="Sélectionner un ou plusieurs mois..."
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                      options={moisOptions}
                      value={selectedMois}
                      onChange={(opts) => setSelectedMois(opts || [])}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="mb-1">Année</Form.Label>
                    <Form.Select value={annee} onChange={(e) => setAnnee(e.target.value)}>
                      <option value="">Toutes les années</option>
                      {[2024, 2025, 2026].map((y) => (<option key={y} value={y}>{y}</option>))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={1} className="d-flex align-items-end">
                  <Button variant="outline-secondary" className="w-100" onClick={resetSelection}>Réinit.</Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="shadow-sm surface-card">
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-5 text-muted">Aucun salaire verrouillé pour cette sélection.</div>
              ) : (
                <div className="table-responsive">
                  <Table className="mb-0 modern-table table-soft">
                    <thead className="table-light">
                      <tr>
                        <th>Employé</th>
                        <th>Matricule</th>
                        <th>Période</th>
                        <th>Date de verrouillage</th>
                        <th>Login verrouilleur</th>
                        <th>Verrouillé par</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.slice((currentPage - 1) * perPage, currentPage * perPage).map((it) => (
                        <tr key={it.id}>
                          <td>{getEmployeName(it)}</td>
                          <td>{getMatricule(it)}</td>
                          <td><Badge bg="secondary">{getPeriode(it)}</Badge></td>
                          <td>{formatDateTimeFr(getLockedAt(it))}</td>
                          <td><Badge bg="light" text="dark">{getLockerLogin(it)}</Badge></td>
                          <td>{getLocker(it)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>

          {filteredItems.length > 0 && (
            <RhTablePagination
              currentPage={currentPage}
              totalItems={filteredItems.length}
              perPage={perPage}
              onPageChange={setCurrentPage}
              onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
              itemLabel="lignes"
            />
          )}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted">{filteredItems.length} ligne(s) verrouillée(s) correspondante(s)</span>
            <Button variant="danger" disabled={!canUnlock || processing} onClick={handleUnlock}>
              {processing ? "Déverrouillage..." : "Déverrouiller et supprimer les bulletins associés"}
            </Button>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default DeverouillerSalairePage;
